import $ from "jquery";

const DROPDOWN_MODE_SELECT = "DROPDOWN_MODE_SELECT"; // Sets the UI placeholder text to display: 'Select State, Select Regions, Select Communitiy'
const DROPDOWN_MODE_FILTER = "DROPDOWN_MODE_FILTER"; // Sets the UI placeholder text to display: 'All States, All Regions, All Communities'

/**
 * This class is responsible for providing the data and events for selecting State, Region (Market Area), and Community
 * where Region dropwdown options are based on the selected State, and the Community options are based on the
 * selected Region.
 */
class StateRegionCommunitySelect {

    /**
     * @param {array} states - states: Array of state objects
     *      Example:
     *      [{Name: 'California', StateCode: 'CA'}, {Name: 'Florida', StateCode: 'FL'}, ...]
     * 
     * @param {object} marketAreasByState - Object representing an associative array where the key is the two char state code and the value is an object containing the state full name and array of market area objects.
     *      Example:
     *      {
     *          'CA': {'StateName': 'California', 'Regions': ['Bay Area', ...]},
     *          'FL': {'Statename': 'Florida', 'Regions': ['Central Florida', ...]},
     *          ...
     *      }
     * 
     * @param {object} communitiesByRegion - Object representing associative array where the key is the region (market area) name and the value is an array of community objects.
     *      Example:
     *      {
     *          'Bay Area': [{'SFCommunityName': 'Beach House at the Dunes', 'Name': 'Beach House at the Dunes', 'City': 'Marina', 'State': 'CA', 'NodeGuid': '00000000-0000-0000-0000-000000000000'}, ...],
     *          ...
     *      }
     * 
     * @param {object} parentForm - JQuery object of the form that contains the dropdown controls.
     * 
     * @param {boolean} renderDropdownsAsMultiSelect - true for multiselect dropdown mode, false for single select
     * 
     * @param {string} dropdownMode - DROPDOWN_MODE_SELECT or DROPDOWN_MODE_FILTER to select display mode with text 'Select *' or 'All *' - see const values above
     */
    
    constructor(states, marketAreasByState, communitiesByRegion, parentForm, renderDropdownsAsMultiSelect = true, dropdownMode = DROPDOWN_MODE_FILTER) {
        this.parentForm = parentForm;
        this.states = states;
        this.marketAreasByState = marketAreasByState;
        this.communitiesByRegion = communitiesByRegion;
        this.communitiesByNodeGuid = {};
        this.communityErrorElem = $($(this.parentForm).find("#divCommunityValidation"));
        this.renderDropdownsAsMultiSelect = renderDropdownsAsMultiSelect;
        if (dropdownMode === DROPDOWN_MODE_FILTER)
        {
            this.defaultText = {
                statePlaceholder: "All States",
                regionPlaceholder: "All Regions",
                communityPlaceholder: "All Communities"
            }
        }
        else
        {
            if (dropdownMode != DROPDOWN_MODE_SELECT)
            {
                console.log("Invalid display mode: " + dropdownMode + ".  Defaulting to DROPDOWN_MODE_SELECT.");
            }

            this.defaultText = {
                statePlaceholder: "Select State",
                regionPlaceholder: "Select Regions",
                communityPlaceholder: "Select Community"
            }
        }

        // Iterate through communitesByRegion properties and create lookup for communitiesByNodeGuid
        for (let key in this.communitiesByRegion) {
            if (Object.prototype.hasOwnProperty.call(this.communitiesByRegion, key)) {
                let communities = this.communitiesByRegion[key];
                communities.forEach((comm) => {
                    this.communitiesByNodeGuid[comm.NodeGuid] = comm;
                });
            }
        }
    }

    /**
     * 
     * @param {string} statesDropdown - JQuery selector syntax for the states select form control
     * @param {string} marketAreasDropdown - JQuery selector syntax for the region (market area) select form control
     * @param {string} communitiesDropdown - JQuery selector syntax for the communities select form control
     * @param {string} pillboxContainer - JQuery selector syntax for the HTML element that contains the selected community pillboxes
     */
    attachData(statesDropdown, marketAreasDropdown, communitiesDropdown, pillboxContainer) {
        this.statesDropdown = $(statesDropdown);
        this.marketAreasDropdown = $(marketAreasDropdown);
        this.communitiesDropdown = $(communitiesDropdown);
        this.pillboxContainer = $(pillboxContainer);

        let clsInstance = this;

        clsInstance.#loadStates();

        this.statesDropdown.change(function () {
            let selectedStateCode = clsInstance.statesDropdown.val();
            if (selectedStateCode) {
                clsInstance.#loadMarketAreaSelectionByState(selectedStateCode);
            }
        });

        this.marketAreasDropdown.change(function () {
            let selectedRegions = clsInstance.marketAreasDropdown.val();
            clsInstance.#loadCommunitiesSelectionByRegion(selectedRegions);
        });

        this.communitiesDropdown.change(function () {
            let selectedCommunities = clsInstance.communitiesDropdown.val();
            // if we are in the multiselect dropdown case, render pillboxes if any communities were selected
            if (selectedCommunities && selectedCommunities.length && selectedCommunities != clsInstance.defaultText.communityPlaceholder) {
                clsInstance.#showCommunityPillboxes(selectedCommunities);
            }
        });

        // Attach this class instance to the validator function. So that it can call methods of this class instance.
        this.communitiesDropdown.get(0).StateRegionCommunitySelect = clsInstance;
    }

    #showCommunityPillboxes(communityNodeGuids) {
        let clsInstance = this;

        // regions is an array in multiselect dropdown case, string in single select case
        // in single select case, convert this string into array of a single string
        if (this.renderDropdownsAsMultiSelect == false ) {
            // regions is an array in multiselect dropdown case, string in single select case
            communityNodeGuids = [communityNodeGuids];
        }

        // Is the community pillbox already displayed
        communityNodeGuids.forEach((communityNodeGuid) => {
            let pillbox = this.pillboxContainer.find('.button-pillbox[data-community-nodeguid="' + communityNodeGuid + '"]');
            if (!pillbox.length) {
                //There is no pillbox being displayed for the community so display it
                let community = this.communitiesByNodeGuid[communityNodeGuid];
                if (community)
                {
                    let pillelem = $('<button></button>')
                    .text(community.Name + ', ' + community.City + (community.State ? ", " + community.State : ''))
                    //.attr('data-communitycode', sfCommunityName)
                    .attr('data-community-nodeguid', communityNodeGuid)
                    .addClass('button-pillbox')
                    .addClass('closeable');

                    this.pillboxContainer.append(pillelem);

                    pillelem.on("click", () => {
                        clsInstance.#removePill($(pillelem));
                        // Refresh the currently selected community dropdown
                        let selectedRegions = clsInstance.marketAreasDropdown.val();
                        if (selectedRegions && selectedRegions.length) {
                            clsInstance.#loadCommunitiesSelectionByRegion(selectedRegions);
                        }
                    })
                }
            }
        });
    }

    #removePill(pill) {
        var nextEl = pill.next();
        pill.remove();

        if (nextEl.length) {
            nextEl.focus();
        } else {
            // console.log("All communities options have been removed");
            var sibling = $(".pillbox-wrapper").siblings('.form-input-container').first();

            this.parentForm.keydown(function (e) {
                if (e.which == 9) {
                    e.preventDefault();
                    $(sibling).find('button, a, input, select, textarea').first().focus();
                    $(this).off(e);
                }
            });
        }
        //sync the community selection with the pills
        this.#syncCommunitySelectionsWithPills();
    }

    #loadStates() {
        let select2Options = [{ id: this.defaultText.statePlaceholder, text: this.defaultText.statePlaceholder }];
        this.states.forEach((state) => {
            select2Options.push(
                { id: state.StateCode, text: state.Name }
            );
        });
        this.statesDropdown.select2({ data: select2Options });
        initCustomDropdowns(this.statesDropdown, this.renderDropdownsAsMultiSelect);
    }

    #loadMarketAreaSelectionByState(stateCode) {
        // Enable the region dropdown
        this.marketAreasDropdown.removeAttr('disabled');
        // Visually hide the tip but leave accessible to screen readers
        this.marketAreasDropdown.siblings('.tip').each(function(_, element) {
            $(element).addClass("show-for-sr");
        });

        // Get the market area for this state
        let marketAreas = '';
        if(this.marketAreasByState[stateCode]!=undefined){
           marketAreas = this.marketAreasByState[stateCode].Regions;
        }

        // 
        let select2Options = [];

        // multi case
        if ( this.renderDropdownsAsMultiSelect ) {
            // Create the region options for multi case
            if (Array.isArray(marketAreas))
            {
                let marketArea = this.marketAreasByState[stateCode];
                if (marketArea)
                {
                    select2Options = [
                        {
                            text: this.marketAreasByState[stateCode].StateName,
                            children: []
                        }
                    ];
                }
                marketAreas.forEach((ma) => {
                    select2Options[0].children.push(
                        {
                            id: ma,
                            text: ma
                        }
                    );
                });
            }
        }
        // single case
        else {
            // Create the region options for single case
            select2Options = [{ id: this.defaultText.regionPlaceholder, text: this.defaultText.regionPlaceholder }];
            if(Array.isArray(marketAreas)) {
                marketAreas.forEach((region) => {
                    select2Options.push(
                        { id: region, text: region }
                    );
                });
            }
        }

        // Clear out the previous region options and set the new options
        this.marketAreasDropdown.empty();
        if (select2Options.length > 0)
        {
            this.marketAreasDropdown.select2({
                data: select2Options
            });
        }
        else
        {
            // No regions are selected, so disable the community dropdown
            this.marketAreasDropdown.attr('disabled', true);
            // Visually show the tip
            this.marketAreasDropdown.siblings('.tip').each(function(_, element) {
                $(element).removeClass("show-for-sr");
            });
        }

        // Apply style to the drop down
        initCustomDropdowns(this.marketAreasDropdown, this.renderDropdownsAsMultiSelect);

        // If there is only one market area, then automatically select it.
        if (this.renderDropdownsAsMultiSelect === false && marketAreas.length !== 1) {
            this.marketAreasDropdown.val("");
        }
        // Check if there is only one market area available
        else if (this.renderDropdownsAsMultiSelect === false && marketAreas.length === 1) {
            // Automatically select the only market area available
            this.marketAreasDropdown.val(marketAreas[0]);
        }
        else if (marketAreas && marketAreas.length == 1) {
            // appears we need to select the first option then select this auto selected value
            this.marketAreasDropdown.val(marketAreas[0]);
        }


        this.marketAreasDropdown.trigger('change');
        
        //Set default selected item when only 1 item to select
        if(this.renderDropdownsAsMultiSelect === false && marketAreas.length === 1)
        {
            var statenewseventsessionindex = 1;
            this.marketAreasDropdown.prop("selectedIndex",statenewseventsessionindex);
            //get id to generate the label selector id for default reion selection  
            var marketAreasDropdown = this.marketAreasDropdown.attr('id');
            $('#select2-'+marketAreasDropdown+'-container').find('span.dropdown-label').html(marketAreas[0]);
        }
        
        //disabled region and community dropdown in case of all state is selected
        if(this.renderDropdownsAsMultiSelect === false && marketAreas.length === 0)
        {
            this.marketAreasDropdown.attr('disabled', true);
            this.communitiesDropdown.attr('disabled', true);

            // Visually show the tip
            this.marketAreasDropdown.siblings('.tip').each(function (_, element) {
                $(element).removeClass("show-for-sr");
            });

            // Visually show the tip
            this.communitiesDropdown.siblings('.tip').each(function (_, element) {
                $(element).removeClass("show-for-sr");
            });
        }
        
        //disbled community dropdown incase of more then 1 region
        if(this.renderDropdownsAsMultiSelect === false && marketAreas.length > 1)
        {
            this.communitiesDropdown.attr('disabled', true);
            // Visually show the tip
            this.communitiesDropdown.siblings('.tip').each(function (_, element) {
                $(element).removeClass("show-for-sr");
            });
        }
    }


    /**
     * 
     * @param {array|string} regions will be an array in multiselect dropdown case, string in single select case
     */
    #loadCommunitiesSelectionByRegion(regions) {
        let clsInstance = this;
        let select2Options = [];

        // regions is an array in multiselect dropdown case, string in single select case
        // in single select case, convert this string into array of a single string
        if (this.renderDropdownsAsMultiSelect == false ) {
            // regions is an array in multiselect dropdown case, string in single select case
            regions = [regions];
        }

        if (regions.length) {
            // Some regions are selected, so enable the community dropdown
            clsInstance.communitiesDropdown.removeAttr('disabled');
            // Visually hide the tip but leave accessible to screen readers
            this.communitiesDropdown.siblings('.tip').each(function(_, element) {
                $(element).addClass("show-for-sr");
            });

        } else {
            // No regions are selected, so disable the community dropdown
            clsInstance.communitiesDropdown.attr('disabled', true);
            // Visually show the tip
            this.communitiesDropdown.siblings('.tip').each(function(_, element) {
                $(element).removeClass("show-for-sr");
            });
        }

        // Multi Select Case
        if (this.renderDropdownsAsMultiSelect) {
            // Regions is an array so loop through all the regions
            // and build the community options
            regions.forEach((region) => {
                let regionGroup = {
                    text: region,
                    children: []
                }

                let communities = clsInstance.communitiesByRegion[region];
                if (communities && communities.length) {
                    communities.forEach((c) => {
                        regionGroup.children.push(
                            {
                                id: c.NodeGuid,
                                text: c.Name + ', ' + c.City + (c.State ? ", " + c.State : '')
                            }
                        );
                    });
                }
                select2Options.push(regionGroup);
            });

        }
        // Single Select case (regions should only contain one)
        else {
            // Create the region options for single case
            select2Options = [{ id: this.defaultText.communityPlaceholder, text: this.defaultText.communityPlaceholder }];

            if (regions.length === 1) {
                let communities = clsInstance.communitiesByRegion[regions[0]];
                if (communities && communities.length) {
                    communities.forEach((c) => {
                        select2Options.push(
                            {
                                id: c.NodeGuid,
                                text: c.Name
                            }
                        );
                    });
                }
            }
            
        }

        // Clear out the current options
        this.communitiesDropdown.empty();

        this.communitiesDropdown.select2({
            data: select2Options
        });

        //attach unselect event. remove corresponding pill when deselected from the options
        this.communitiesDropdown.on('select2:unselect', (e) => {
            let pillbox = clsInstance.pillboxContainer.find('.button-pillbox[data-community-nodeguid="' + e.params.data.id + '"]');
            clsInstance.#removePill(pillbox);
        });

        initCustomDropdowns(this.communitiesDropdown, this.renderDropdownsAsMultiSelect);

        this.communitiesDropdown.trigger('change');

        //Set community disabled if there is no communities is available on the selected region
        if(this.renderDropdownsAsMultiSelect === false && regions.length === 1 && regions[0] === this.defaultText.regionPlaceholder)
        {
            this.communitiesDropdown.attr("disabled","");
            // Visually show the tip
            this.communitiesDropdown.siblings('.tip').each(function(_, element) {
                $(element).removeClass("show-for-sr");
            });
        }
        
        //Disable region and community dropdown in case ofall state is selected
        if(this.renderDropdownsAsMultiSelect === false && regions.length === 0)
        {
            this.communitiesDropdown.attr('disabled', true);
            // Visually show the tip
            this.communitiesDropdown.siblings('.tip').each(function(_, element) {
                $(element).removeClass("show-for-sr");
            });
        }

        //sync the community selection with the pills
        this.#syncCommunitySelectionsWithPills();
    }

    getSelectedPillSFCommunityNames() {
        let sfCommunityNames = [];
        let pillboxes = this.pillboxContainer.find('.button-pillbox');
        pillboxes.each((idx) => {
            let pill = pillboxes[idx];
            let pilldatanodeguid = $(pill).data('community-nodeguid');
            if (pilldatanodeguid) {
                let comm = this.communitiesByNodeGuid[pilldatanodeguid];
                if (comm)
                {
                    if (typeof comm.SFCommunityName === 'string' && comm.SFCommunityName.trim().length > 0)
                    {
                        sfCommunityNames.push(comm.SFCommunityName);
                    }
                    else
                    {
                        sfCommunityNames.push(comm.Name);
                    }
                }
            }
        });

        return sfCommunityNames;
    }

    getSelectedPillSFCommunityNodeGuids() {
        let sfCommunityNodeGuids = [];
        let pillboxes = this.pillboxContainer.find('.button-pillbox');
        pillboxes.each((idx) => {
            let pill = pillboxes[idx];
            let pilldatanodeguid = $(pill).data('community-nodeguid');
            if (pilldatanodeguid) {
                sfCommunityNodeGuids.push(pilldatanodeguid);
            }
        });
        return sfCommunityNodeGuids;
    }

    #syncCommunitySelectionsWithPills() {
        let selectedValues = this.getSelectedPillSFCommunityNodeGuids();
        this.communitiesDropdown.val(selectedValues).change();
    }

    showFormErrorChildren($parent, className) {
        var children = $parent.children(".form-error");
        if (children.length) {
            for (var i = 0; i < children.length; i++) {
                if (children[i].classList.contains(className)) {
                    children[i].classList.remove("is-hidden");
                } else {
                    children[i].classList.add("is-hidden");
                }
            }
        }
    }

    // If the url contains a community parameter, show the community in the Pillbox
    setDropdowmsFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const communityNodeGuidParam = urlParams.get("community");
        if (communityNodeGuidParam) {
            let community = this.getCommunityData(communityNodeGuidParam);
            if (community) {
                // Show the community in the Pillbox
                this.#showCommunityPillboxes([community.nodeGuid]);
            }
            else {
                console.error('Community specified in parameter is not found in dropdowns: ' + communityName);
            }
        }
    }

    // Get the community data, return it's state and region (and name)
    getCommunityData(communityNodeGuid) {
        for (const region in this.communitiesByRegion) {
            const communities = this.communitiesByRegion[region];
            const community = communities.find((c) => c.NodeGuid === communityNodeGuid);
            if (community) {
                return {
                    state: community.State,
                    region: region,
                    communityName: community.Name,
                    sfCommunityName: community.SFCommunityName,
                    nodeGuid: community.NodeGuid
                };
            }
        }
        return null; // Return null if the community is not found
    }
}

Foundation.Abide.defaults.validators['validate_community_selection'] = function (el, required, parent) {
    // Get the attached instance of the StateRegionCommunitySelect object that was set as a property of this function,
    // and get the selected communities.

    function getElementByIdAttribute(el, dataIdAttrName) {
        let id = el.attr(dataIdAttrName);
        if (id) { return $("#" + id); }
        return undefined;
    }

    let fieldType = el.attr("data-field-type");

    let elState = getElementByIdAttribute(el, "data-state-id");
    if (elState) elState.removeClass("is-invalid-input");

    let elRegion = getElementByIdAttribute(el, "data-region-id");
    if (elRegion) elRegion.removeClass("is-invalid-input");

    let elCommunity = getElementByIdAttribute(el, "data-community-id");
    if (elCommunity) {
        elCommunity.removeClass("is-invalid-input");
    }
    else if (fieldType == "community-field") {
        elCommunity = el;
    }

    let clsInstance = elCommunity.get(0).StateRegionCommunitySelect;
    let selectedCommunities = clsInstance.communitiesDropdown.val();
    let communityPills = clsInstance.getSelectedPillSFCommunityNames();

    let isCommunitySelected = ((communityPills && communityPills.length > 0) || (selectedCommunities && selectedCommunities.length > 0));

    switch (fieldType) {
        case "message-field": // triggers when submit is clicked
            {
                // Set validation-active on the region and community selects.
                // This indicates that validation is active.
                // If the validation is not active, skip the validation checks (later in the code).
                elRegion.prop("validation-active", true);
                elCommunity.prop("validation-active", true);
                if (!elState.val() || elState.val().length == 0) {
                    elState.addClass('is-invalid-input');
                }
                if (!elRegion.val() || elRegion.val().length == 0) {
                    if (el.prop("disabled")) {
                        elRegion.addClass('is-invalid-input');
                    }
                }
                if (!elCommunity.val() || elCommunity.val().length == 0) {
                    elCommunity.addClass('is-invalid-input');
                }
                //fall through to display error message
            }
            break;

        case "state-field":
            {
                if (el.val()!=null && el.val()!=undefined && (el.val().indexOf("Select") > 0 || el.val() == '' || el.val().length == 0)) {
                    if (!isCommunitySelected) el.addClass('is-invalid-input');
                    return isCommunitySelected;
                }
                else {
                    return true;
                };
            }
            break;
        case "region-field":
            {
                // If no value is selected & validation is active (submit clicked)
                if (el.val()!=null && el.val()!=undefined && (el.val().indexOf("Select") > 0 || el.val() == '' || el.val().length == 0) &&
                    el.prop("validation-active")) {
                    if (!isCommunitySelected) el.addClass('is-invalid-input');
                    return isCommunitySelected;
                }
                else {
                    return true;
                }
            }
            break;
        case "community-field":
            {
                if (!el.prop("validation-active")) {
                    return true;
                }
                // fall through
            }
            break;
    }

    if (!isCommunitySelected) {
        clsInstance.showFormErrorChildren(parent, "invalid-required");
        el.attr("aria-invalid", "true");

        // Display the community error markup
        if (clsInstance.communityErrorElem) {
            clsInstance.communityErrorElem.css("visibility", "visible");
        }
    }
    else {
        // Hide the community error markup
        if (clsInstance.communityErrorElem) {
            clsInstance.communityErrorElem.css("visibility", "hidden");
        }
    }
    return isCommunitySelected;
};

export { StateRegionCommunitySelect as default, DROPDOWN_MODE_SELECT, DROPDOWN_MODE_FILTER };
